import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import AccessGate from "@/components/AccessGate";
import Header from "@/components/Header";
import NeedCard from "@/components/NeedCard";
import PostNeedDialog from "@/components/PostNeedDialog";
import VolunteerDialog from "@/components/VolunteerDialog";
import DailyDevotional from "@/components/DailyDevotional";
import BulletinsSection from "@/components/BulletinsSection";
import AnnouncementsSection from "@/components/AnnouncementsSection";
import { Plus, Shield, BookOpen, Loader2 } from "lucide-react";

export default function Home() {
  const [unlocked, setUnlocked] = useState(false);
  const [needs, setNeeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [postOpen, setPostOpen] = useState(false);
  const [helpNeed, setHelpNeed] = useState(null);

  useEffect(() => {
    if (localStorage.getItem("group_access_ok") === "1") setUnlocked(true);
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/needs");
      setNeeds(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (unlocked) load();
  }, [unlocked]);

  if (!unlocked) return <AccessGate onUnlock={() => setUnlocked(true)} />;

  const activeNeeds = needs.filter((n) => !n.archived);
  const prayerNeeds = activeNeeds.filter((n) => n.category === "Petición de Oración");
  const otherNeeds = activeNeeds.filter((n) => n.category !== "Petición de Oración");

  return (
    <div className="min-h-screen bg-paper">
      <Header />

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-10 space-y-8">
        {/* Mensaje Diario del Pastor */}
        <DailyDevotional />

        {/* Boletín Digital */}
        <BulletinsSection />

        {/* Anuncios */}
        <AnnouncementsSection />

        {/* Botones de acción */}
        <div className="flex flex-col sm:flex-row items-stretch gap-3">
          <Button
            data-testid="post-need-button"
            onClick={() => setPostOpen(true)}
            className="flex-1 h-16 text-lg font-semibold bg-[#8A3A33] hover:bg-[#6B2B25] text-white rounded-xl shadow-md"
          >
            <Plus className="w-6 h-6 mr-2" />
            Publicar una Necesidad
          </Button>
          <Link to="/pastor" className="sm:w-auto">
            <Button
              data-testid="pastor-link"
              variant="outline"
              className="w-full h-16 px-5 text-base border-2 border-[#D1C9C1] text-[#2C5545] hover:bg-[#EAF2EE]"
            >
              <BookOpen className="w-5 h-5 mr-2" />
              Pastor
            </Button>
          </Link>
          <Link to="/admin" className="sm:w-auto">
            <Button
              data-testid="admin-link"
              variant="outline"
              className="w-full h-16 px-5 text-base border-2 border-[#D1C9C1] text-[#4A4646] hover:bg-[#F3F1EF]"
            >
              <Shield className="w-5 h-5 mr-2" />
              Admin
            </Button>
          </Link>
        </div>

        {/* Necesidades activas */}
        <section>
          <div className="flex items-baseline justify-between mb-5">
            <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#1A1818]">
              Necesidades Activas
            </h2>
            <span className="text-base text-[#4A4646] font-medium">
              {otherNeeds.length} {otherNeeds.length === 1 ? "necesidad" : "necesidades"}
            </span>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#8A3A33]" />
            </div>
          ) : otherNeeds.length === 0 ? (
            <div
              data-testid="empty-state"
              className="bg-white rounded-2xl border-2 border-dashed border-[#D1C9C1] p-10 text-center"
            >
              <p className="font-serif text-xl text-[#1A1818] mb-2">No hay necesidades activas</p>
              <p className="text-base text-[#4A4646]">Sea el primero en compartir una necesidad con el grupo.</p>
            </div>
          ) : (
            <div className="space-y-5">
              {otherNeeds.map((n) => (
                <NeedCard key={n.id} need={n} onHelp={setHelpNeed} />
              ))}
            </div>
          )}
        </section>

        {/* Solicitudes de Oración */}
        {prayerNeeds.length > 0 && (
          <section data-testid="prayer-section">
            <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#1A1818] mb-5 flex items-center gap-2">
              🙏 Solicitudes de Oración
            </h2>
            <div className="space-y-5">
              {prayerNeeds.map((n) => (
                <NeedCard key={n.id} need={n} onHelp={setHelpNeed} />
              ))}
            </div>
          </section>
        )}

        <footer className="mt-12 pb-8 text-center">
          <p className="font-serif italic text-base text-[#4A4646]">
            &ldquo;Servíos por amor los unos a los otros.&rdquo; — Gálatas 5:13
          </p>
        </footer>
      </main>

      <PostNeedDialog open={postOpen} onOpenChange={setPostOpen} onCreated={load} />
      <VolunteerDialog
        need={helpNeed}
        onClose={() => setHelpNeed(null)}
        onDone={load}
      />
    </div>
  );
}
